import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import { shouldReduceExperience } from '../../lib/performance';
type Variant = 'primary' | 'emergency' | 'ghost';
type Props = {
  children: React.ReactNode;
  variant?: Variant;
  to?: string;
  href?: string;
  onClick?: () => void;
  className?: string;
  strength?: number;
};
export function MagneticButton({
  children,
  variant = 'primary',
  to,
  href,
  onClick,
  className = '',
  strength = 0.35
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const pendingPosition = useRef({
    x: 0,
    y: 0
  });
  const [magneticEnabled, setMagneticEnabled] = useState(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, {
    stiffness: 250,
    damping: 18
  });
  const springY = useSpring(y, {
    stiffness: 250,
    damping: 18
  });
  useEffect(() => {
    setMagneticEnabled(window.matchMedia('(pointer: fine)').matches && !shouldReduceExperience());
  }, []);
  const handleMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!magneticEnabled || !ref.current) return;
    const bounds = ref.current.getBoundingClientRect();
    pendingPosition.current = {
      x: (event.clientX - (bounds.left + bounds.width / 2)) * strength,
      y: (event.clientY - (bounds.top + bounds.height / 2)) * strength
    };
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      x.set(pendingPosition.current.x);
      y.set(pendingPosition.current.y);
      frameRef.current = null;
    });
  };
  const reset = () => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    x.set(0);
    y.set(0);
  };
  const base = 'relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full px-8 py-4 text-sm font-semibold tracking-wide shimmer-sweep transition-shadow duration-300';
  const styles: Record<Variant, string> = {
    primary: 'bg-royal text-white shadow-glow hover:shadow-[0_25px_70px_-15px_rgba(0,87,255,0.6)]',
    emergency: 'bg-fire text-white shadow-fire hover:shadow-[0_25px_70px_-12px_rgba(255,59,48,0.6)]',
    ghost: 'glass-strong text-navy hover:text-royal'
  };
  const fill = variant === 'emergency' ? 'bg-crimson' : variant === 'ghost' ? 'bg-sky' : 'bg-electric';
  const content = <motion.div ref={ref} onMouseMove={handleMove} onMouseLeave={reset} style={magneticEnabled ? {
    x: springX,
    y: springY
  } : undefined} data-cursor="button" className="group inline-block" data-id="element-91" data-mp="3tx96sir29div">
      <span className={`${base} ${styles[variant]} ${className}`} data-id="element-92" data-mp="23jiat1o0b0tf">
        <span className={`absolute inset-0 -z-0 origin-bottom scale-y-0 rounded-full ${fill} opacity-90 transition-transform duration-500 ease-out group-hover:scale-y-100`} data-id="element-93" data-mp="28jejvfbk9ifu" />
        <span className="relative z-10 flex items-center gap-2" data-id="element-94" data-mp="ln6w1qok12zx">
          {children}
        </span>
      </span>
    </motion.div>;
  if (to) return <Link to={to} data-id="element-95" data-mp="zleqnry4bs7x">{content}</Link>;
  if (href) return <a href={href} data-id="element-96" data-mp="vl4h65b4ajob">{content}</a>;
  return <button type="button" onClick={onClick} data-id="element-97" data-mp="vmfm48gatxh0">
      {content}
    </button>;
}